import { apiGet } from "../client";
import { useQuery } from "@tanstack/react-query";
import { QueryKeys } from "@/lib/query-keys";
import type { IQuestionHighlight } from "./types";

/**
 * 문제 하이라이트 정보를 가져옵니다.
 * @param examProgressId 시험 진행 ID
 * @param questionId 문제 ID (passage_id가 없을 때)
 * @param passageId 지문 ID (shared passage일 때)
 * @returns API 응답
 */
export const apiGetHighlights = async (
  examProgressId: string,
  questionId?: string,
  passageId?: string
) => {
  let url = `/highlights?exam_progress_id=${examProgressId}`;

  if (passageId) {
    url += `&passage_id=${passageId}`;
  } else if (questionId) {
    url += `&question_id=${questionId}`;
  } else {
    throw new Error("Either questionId or passageId must be provided");
  }

  const response = await apiGet<IQuestionHighlight>(url);
  return response;
};

/**
 * 문제 하이라이트 정보를 React Query를 통해 가져오는 훅
 * @param examProgressId 시험 진행 ID
 * @param questionId 문제 ID (passage_id가 없을 때)
 * @param passageId 지문 ID (shared passage일 때)
 * @param options React Query 옵션
 * @returns 쿼리 결과
 */
export const useQueryGetHighlights = (
  examProgressId: string,
  questionId?: string,
  passageId?: string,
  options = {}
) => {
  const queryKey = passageId
    ? ([
        ...QueryKeys.question.all,
        "highlights",
        examProgressId,
        "passage",
        passageId,
      ] as const)
    : ([
        ...QueryKeys.question.all,
        "highlights",
        examProgressId,
        "question",
        questionId || "",
      ] as const);

  return useQuery({
    queryKey,
    queryFn: async () => {
      const response = await apiGetHighlights(
        examProgressId,
        questionId,
        passageId
      );
      return response.data;
    },
    ...options,
    enabled: !!examProgressId && (!!questionId || !!passageId),
  });
};

export default useQueryGetHighlights;
